import { CheckIcon } from "@heroicons/react/24/outline"
import classNames from "classnames"
import { motion } from "framer-motion"

interface Props {
  className?: string

  /**
   * The variant of the spinner.
   *
   * Default: Circle that rotates (with a tiny gap)
   * Dots: Loading dots "..."
   * Spinner: Spinning Lines around the center
   * Checkmark: Checkmark that grows in size
   */
  variant?: "default" | "dots" | "spinner" | "checkmark"
}

/**
 * Shows an animated loading "spinner" (depending on the variant chosen)
 */
export const Spinner = ({ className, variant = "default" }: Props) => {
  switch (variant) {
    case "dots":
      return (
        <svg
          className={classNames("size-5", className)}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
        >
          <circle fill="currentColor" stroke="currentColor" strokeWidth="1.8" r="1.8" cx="4.8" cy="12">
            <animate
              attributeName="opacity"
              calcMode="spline"
              dur="2"
              values="1;0;1;"
              keySplines=".5 0 .5 1;.5 0 .5 1"
              repeatCount="indefinite"
              begin="-.4"
            />
          </circle>
          <circle fill="currentColor" stroke="currentColor" strokeWidth="1.8" r="1.8" cx="12" cy="12">
            <animate
              attributeName="opacity"
              calcMode="spline"
              dur="2"
              values="1;0;1;"
              keySplines=".5 0 .5 1;.5 0 .5 1"
              repeatCount="indefinite"
              begin="-.2"
            />
          </circle>
          <circle fill="currentColor" stroke="currentColor" strokeWidth="1.8" r="1.8" cx="19.2" cy="12">
            <animate
              attributeName="opacity"
              calcMode="spline"
              dur="2"
              values="1;0;1;"
              keySplines=".5 0 .5 1;.5 0 .5 1"
              repeatCount="indefinite"
              begin="0"
            />
          </circle>
        </svg>
      )

    case "spinner":
      return (
        <svg
          className={classNames("size-5 animate-spin-slow", className)}
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 2V6 M12 18V22 M4.93 4.93L7.76 7.76 M16.24 16.24L19.07 19.07 M2 12H6 M18 12H22 M4.93 19.07L7.76 16.24 M16.24 7.76L19.07 4.93"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )

    case "checkmark":
      return (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 400, damping: 14 }}
        >
          <CheckIcon className={classNames("size-5 animate-checkmark", className)} />
        </motion.div>
      )

    case "default":
      return (
        <svg
          className={classNames("size-5 animate-spin", className)}
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="2"
            strokeDasharray="44"
            strokeDashoffset="10"
            strokeLinecap="round"
          />
        </svg>
      )
  }
}
